import { PLinkPure, PPopover, PText } from "@porsche-design-system/components-react"
import type { InventoryInformationView } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import type { JSX } from "react"
import { defineMessages, useIntl } from "react-intl"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { Date, Flex, List } from "src/components"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { VehicleOrderStatus } from "src/screens/vehicleDetails/overviewContainer/components/VehicleOrderStatus"
import { EditButton } from "../editButton/EditButton"

const messages = defineMessages({
	commissionNumber: {
		defaultMessage: "Commission No.",
		id: "vehicleDetails.overview.summary.commissionNumber",
	},
	inventoryInformationTitle: {
		defaultMessage: "Inventory information",
		id: "vehicleDetails.overview.summary.inventoryInformationTitle",
	},
	inventoryNumber: {
		defaultMessage: "Inventory Number",
		id: "vehicle.inventoryNumber",
	},
	orderedAs: {
		defaultMessage: "Ordered as",
		id: "vehicleDetails.overview.summary.orderedAs",
	},
	vehicleStatus: {
		defaultMessage: "Vehicle Status",
		id: "vehicleDetails.overview.summary.vehicleStatus",
	},
	vin: {
		defaultMessage: "VIN",
		id: "vehicleDetails.overview.summary.vin",
	},
})

interface InventoryInformationListProps {
	commissionNumber: string | undefined
	inventoryInformationView: InventoryInformationView | undefined | null
	newCarOrderId: string | undefined
	onEditClick: (stage: VehicleDetailsStage) => void
	orderedAs: string | undefined
	vin: string
}
export const InventoryInformationList = (props: InventoryInformationListProps) => {
	const { formatMessage } = useIntl()
	const routes = useDealerAppRoutes()
	const { commissionNumber, inventoryInformationView, newCarOrderId, onEditClick, orderedAs, vin } = props

	const vehicleStatusHint = useMemo<JSX.Element[] | undefined>(() => {
		const inventoryStatus = inventoryInformationView?.inventoryAndIntake.inventoryStatus
		const statusItems = [inventoryStatus?.pvmsStatusHint, inventoryStatus?.currentVehicleLocation].filter(
			(i) => !!i,
		)
		return statusItems.length === 0 ? undefined : statusItems.map((i) => <div key={i}>{i}</div>)
	}, [inventoryInformationView?.inventoryAndIntake.inventoryStatus])

	return (
		<List header={formatMessage(messages.inventoryInformationTitle)}>
			{inventoryInformationView && (
				<List.Item
					label={formatMessage(messages.inventoryNumber)}
					value={inventoryInformationView.inventoryAndIntake.inventoryNumber}
					valueSingleLine
				/>
			)}
			<List.Item
				data-testid="vehicle-details-overview-summary_vin"
				label={formatMessage(messages.vin)}
				value={vin}
				valueSingleLine
			/>
			{commissionNumber && (
				<List.Item label={formatMessage(messages.commissionNumber)} value={commissionNumber} valueSingleLine />
			)}
			{newCarOrderId && (
				<List.Item
					label="New Car Order ID"
					value={
						<PLinkPure
							alignLabel="start"
							target="_blank"
							href={routes?.external?.oneVms.orderDetails.url(newCarOrderId)}
							icon="external"
							title={newCarOrderId}
						>
							<PText weight="semibold">{newCarOrderId}</PText>
						</PLinkPure>
					}
					valueSingleLine
				/>
			)}
			{orderedAs && <List.Item label={formatMessage(messages.orderedAs)} value={orderedAs} valueSingleLine />}
			{inventoryInformationView && (
				<>
					<List.Item
						label={formatMessage(messages.vehicleStatus)}
						value={
							<Flex $gap="xs">
								<List.ItemValue
									value={
										inventoryInformationView.inventoryAndIntake.inventoryStatus
											.inventoryStatusMessage
									}
								/>
								{vehicleStatusHint && <PPopover>{vehicleStatusHint}</PPopover>}
								{inventoryInformationView.inventoryAndIntake.inventoryEdit && (
									<EditButton
										onClick={() => onEditClick("inventoryIntake")}
										actionItem={inventoryInformationView.inventoryAndIntake.inventoryEdit}
									/>
								)}
							</Flex>
						}
					/>
					{inventoryInformationView.inventoryAndIntake.vehicleOrder && (
						<List.Item
							label="Order Status Code"
							value={
								<VehicleOrderStatus view={inventoryInformationView.inventoryAndIntake.vehicleOrder} />
							}
						/>
					)}
					{inventoryInformationView.inventoryAndIntake.inventoryStatus.availabilityDate && (
						<List.Item
							label={inventoryInformationView.inventoryAndIntake.inventoryStatus.availabilityDate.label}
							value={
								<Date
									weight="semibold"
									view={
										inventoryInformationView.inventoryAndIntake.inventoryStatus.availabilityDate
											.date
									}
								/>
							}
							valueSingleLine
						/>
					)}
				</>
			)}
		</List>
	)
}
